import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Button from '../components/common/Button';
import { RecipeContext } from '../contexts/RecipeContext';
import { Recipe } from '../types/Recipe';

const RecipeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getRecipeById } = useContext(RecipeContext);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (id) {
      const recipeData = getRecipeById(id);
      if (recipeData) {
        setRecipe(recipeData);
      }
      setLoading(false);
    }
  }, [id, getRecipeById]);

  const handleEdit = () => {
    navigate(`/edit-recipe/${id}`);
  };

  const handleBack = () => {
    navigate('/recipes');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow py-20 bg-light flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow py-20 bg-light">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-6" data-aos="fade-up">Recette non trouvée</h1>
            <p className="text-gray-600 mb-8" data-aos="fade-up" data-aos-delay="100">
              La recette que vous recherchez n'existe pas ou a été supprimée.
            </p>
            <Button variant="primary" onClick={handleBack} data-aos="fade-up" data-aos-delay="200">
              Retourner à mes recettes
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow py-10 md:py-20 bg-light">
        <div className="container mx-auto px-4">
          <div className="mb-6 flex justify-between items-center" data-aos="fade-in">
            <button 
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-primary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Retour aux recettes
            </button>
            
            <Button variant="secondary" onClick={handleEdit} className="hover-scale">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Modifier la recette
            </Button>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-aos="fade-up">
            {/* Image de la recette */}
            <div className="h-48 sm:h-64 md:h-80 bg-gray-200 relative">
              {recipe.imageUrl ? (
                <img 
                  src={recipe.imageUrl} 
                  alt={recipe.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              
              <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                {recipe.category}
              </div>
            </div>
            
            {/* Contenu de la recette */}
            <div className="p-4 md:p-8">
              <h1 className="text-2xl md:text-3xl font-bold mb-2" data-aos="fade-up">{recipe.title}</h1>
              
              <div className="flex flex-wrap gap-3 md:gap-4 mb-6" data-aos="fade-up" data-aos-delay="100">
                <div className="flex items-center text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span>Préparation: {recipe.prepTime} min</span>
                </div>
                
                <div className="flex items-center text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span>Cuisson: {recipe.cookTime} min</span>
                </div>
                
                <div className="flex items-center text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <span>{recipe.servings} personne{recipe.servings > 1 ? 's' : ''}</span>
                </div>
                
                <div className="flex items-center text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                  <span>Difficulté: {recipe.difficulty}</span>
                </div>
              </div>
              
              {recipe.description && (
                <div className="mb-8" data-aos="fade-up" data-aos-delay="200">
                  <h2 className="text-xl font-semibold mb-3">Description</h2>
                  <p className="text-gray-700">{recipe.description}</p>
                </div>
              )}
              
              <div className="mb-8" data-aos="fade-up" data-aos-delay="300">
                <h2 className="text-xl font-semibold mb-3">Ingrédients</h2>
                <ul className="list-disc pl-5 space-y-2">
                  {recipe.ingredients.map((ingredient, index) => (
                    <li key={index} className="text-gray-700 stagger-item">{ingredient}</li>
                  ))}
                </ul>
              </div>
              
              <div data-aos="fade-up" data-aos-delay="400">
                <h2 className="text-xl font-semibold mb-3">Préparation</h2>
                <ol className="list-decimal pl-5 space-y-4">
                  {recipe.steps.map((step, index) => (
                    <li key={index} className="text-gray-700 stagger-item">
                      <p>{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-500" data-aos="fade-up" data-aos-delay="500">
                <p>Ajoutée le {new Date(recipe.createdAt).toLocaleDateString()}</p>
                {recipe.updatedAt && recipe.updatedAt !== recipe.createdAt && (
                  <p>Dernière modification le {new Date(recipe.updatedAt).toLocaleDateString()}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default RecipeDetailPage;
